import type { SupabaseClient } from '../client'
import type { Initiative } from '../domain/initiative'
import { unwrapList, unwrapMaybe } from './_helpers'

const INITIATIVE_COLUMNS =
  'id, project_id, name, description, phase, current_phase, initial_phase, status, status_reason, priority, sort_order, owner_id, start_date, end_date, delivered_at, current_sprint, is_continuous, archived, t_shirt_size, tipo_iniciativa, business_objective, sized_at, parent_core_initiative_id, repo_url, docs_content, scope_content, processes_content, created_at'

/**
 * Lista as initiatives de um projeto, ordenadas por sort_order.
 */
export async function listInitiativesByProject(
  client: SupabaseClient,
  projectId: number,
): Promise<Initiative[]> {
  const result = await client
    .schema('operacoes')
    .from('initiatives')
    .select(INITIATIVE_COLUMNS)
    .eq('project_id', projectId)
    .order('sort_order', { ascending: true, nullsFirst: false })
  return unwrapList(result) as unknown as Initiative[]
}

/**
 * Carrega uma initiative pelo ID. Retorna `null` se nao existe ou se o
 * cliente nao tem acesso (RLS bloqueia).
 */
export async function getInitiativeById(
  client: SupabaseClient,
  initiativeId: number,
): Promise<Initiative | null> {
  const result = await client
    .schema('operacoes')
    .from('initiatives')
    .select(INITIATIVE_COLUMNS)
    .eq('id', initiativeId)
    .maybeSingle()
  return unwrapMaybe(result) as unknown as Initiative | null
}

const INITIATIVE_PHASE_COLUMNS =
  'id, initiative_id, phase, status, started_at, completed_at, sort_order'

export type InitiativePhaseRow = {
  id: number
  initiative_id: number
  phase: string
  status: string
  started_at: string | null
  completed_at: string | null
  sort_order: number | null
}

/**
 * As fases vividas por uma iniciativa (`operacoes.initiative_phases`).
 *
 * Esta é a máquina de fases REAL, a mesma que o ERP move e que alimenta
 * `initiatives.current_phase`. Não confundir com `initiatives.phase`, que é
 * um campo fóssil: nenhum código nem trigger escreve nele desde a criação da
 * iniciativa, e por isso 38 das 47 iniciativas vivas o mantêm em
 * 'solution_flow' mesmo já estando em execução (medido em 10/09/2026).
 */
export async function listInitiativePhases(
  client: SupabaseClient,
  initiativeId: number,
): Promise<InitiativePhaseRow[]> {
  const result = await client
    .schema('operacoes')
    .from('initiative_phases')
    .select(INITIATIVE_PHASE_COLUMNS)
    .eq('initiative_id', initiativeId)
    .order('sort_order', { ascending: true, nullsFirst: false })
  return unwrapList(result) as unknown as InitiativePhaseRow[]
}
